import {
    BaseInstance,
    DrawMode,
    DrawModeMap,
    Edge,
    GroupNode,
    LeafNode,
    Light,
    LightLeafNode,
    Model,
    NodeType
} from './types';
import {mat4, vec4} from 'gl-matrix';
import {
    generateCubeInstance,
    generateCylinderInstance,
    generateFanInstance,
    generateRoomInstance,
    generateSemisphereInstance,
    generateSphereInstance
} from './BaseInstance';
import {TextureObject} from '%COMMON/TextureObject';

/**
 * Class in charge of rendering the SceneGraph model.
 */
export class Renderer {
    private readonly idToInstance: BaseInstance[];
    private readonly gl: WebGLRenderingContext;
    private readonly proj: mat4;
    private readonly textureMatrix: mat4;
    private readonly notFoundTexture: TextureObject;
    private readonly textureMap: { [url: string]: TextureObject };
    private lights: Light[];
    private drawMode: DrawMode;
    private drawMap: DrawModeMap;

    /**
     * Sets up the Renderer object
     * You MUST set the shader locations via the setShaderLocations method to use anything except the lightpass
     * with the initial flag set to true
     * @param gl the {@link WebGLRenderingContext} to use
     * @param proj the {@link mat4} projection matrix to use
     */
    constructor(gl: WebGLRenderingContext, proj: mat4, textureMatrix: mat4, notFoundTexture: TextureObject, drawMode: DrawMode, drawMap: DrawModeMap) {
        this.gl = gl;
        this.idToInstance = [
            generateCubeInstance(gl),
            generateCylinderInstance(gl),
            generateSphereInstance(gl),
            generateSemisphereInstance(gl),
            generateFanInstance(gl),
            generateRoomInstance(gl),
        ]
        this.proj = proj;
        this.textureMatrix = textureMatrix;
        this.textureMap = {};
        this.lights = [];
        this.notFoundTexture = notFoundTexture;
        this.drawMap = drawMap;
        this.drawMode = drawMode;
    }

    private getTexture = (name: string, url: string): WebGLTexture => {
        if (!this.textureMap.hasOwnProperty(url)) {
            this.textureMap[url] = new TextureObject(this.gl, name, url);
        }
        return this.textureMap[url].getTextureID();
    }


    public setDrawMode(mode: DrawMode) {
        this.drawMode = mode;
    }

    /**
     * Renders the leaf node of a Scenegraph representation. Uses the accumulated
     * modelView object in order to transform the base instance to how it should be
     * shown in the world.
     */
    private renderLeafNode(modelView: mat4, leafNode: LeafNode) {
        const {drawFn} = this.drawMap.get(this.drawMode);
        drawFn(modelView, leafNode, this.getTexture, this.notFoundTexture, this.textureMatrix, this.proj, this.idToInstance);
    };

    /**
     * Renders a group node, which applies a transformation onto multiple child group or leaf
     * nodes.
     */
    private renderGroupNode(modelView: mat4, groupNode: GroupNode, time: number, nodeHandler: (modelView: mat4, node: GroupNode | LeafNode | LightLeafNode, nodeType: NodeType, time: number) => void) {
        groupNode.edges.forEach((edge: Edge) => {
            const childMatrix: mat4 = mat4.clone(modelView);
            edge.transformation && mat4.multiply(childMatrix, childMatrix, edge.transformation);
            edge.animation && mat4.multiply(childMatrix, childMatrix, edge.animation(time));
            nodeHandler(childMatrix, edge.to, edge.toType, time);
        });
    };

    /**
     * Renders an arbitrary node (based on whether it's a leaf node or a group node) LightLeafNodes are ignored here as they are handled in a separate, preceeding, pass.
     */
    private renderNode = (modelView: mat4, node: GroupNode | LeafNode | LightLeafNode, nodeType: NodeType, time: number) => {
        if (nodeType === NodeType.GROUP_NODE) {
            this.renderGroupNode(modelView, node as GroupNode, time, this.renderNode);
        } else if (nodeType === NodeType.LEAF_NODE) {
            this.renderLeafNode(modelView, node as LeafNode);
        }
    }

    private renderNodeLightPass = (modelView: mat4, node: GroupNode | LeafNode | LightLeafNode, nodeType: NodeType, time: number) => {
        if (nodeType === NodeType.GROUP_NODE) {
            this.renderGroupNode(modelView, node as GroupNode, time, this.renderNodeLightPass);
        } else if (nodeType === NodeType.LIGHT_LEAF_NODE) {
            this.setupLight(modelView, node as LightLeafNode);
        }
    }

    /**
     *
     * @param modelView the current modelview matrix
     * @param lightNode the {@link LightLeafNode} to transform and push to the renderer lights
     */
    private setupLight(modelView: mat4, lightNode: LightLeafNode) {
        let lightCopy: Light = {...lightNode.light};
        lightCopy.position = vec4.transformMat4(vec4.create(), lightCopy.position, modelView);
        lightCopy.spotTarget = vec4.transformMat4(vec4.create(), lightCopy.spotTarget, modelView);
        this.lights.push(lightCopy);
    }

    /**
     * Render a model with a initial modelView (which will most likely represent the camera
     * view of the scene.
     */
    public render(modelView: mat4, model: Model, time: number = 0): void {
        if (!this.drawMap.get(this.drawMode)) throw new Error(`Draw Mode ${this.drawMode} must be defined if not initial pass`);
        this.renderNode(modelView, model.scene, model.sceneNodeType, time);
    };

    /**
     * Makes a pass of the scenegraph to set up lights into the view coordinate system so that we can do lighting effects
     * This method, when called, replaces the lights currently active with new ones.
     * @param modelView The {@link mat4} containing the base modelview matrix to transform the lightPositions
     * @param model The {@link Model} representing the scenegraph to traverse
     * @param time The current time value of the program
     * @returns the number of lights in the scene. Includes both view and object space lights
     */
    public lightPass(modelView: mat4, model: Model, time: number = 0, initialPass: boolean = false): number {
        // clear the lights
        this.lights = [];
        //add the view space lights to the list
        model.viewSpaceLights && this.lights.push(...model.viewSpaceLights);
        // traverse the tree for the world/object space lights
        this.renderNodeLightPass(modelView, model.scene, model.sceneNodeType, time);

        if (!initialPass) {
            if (!this.drawMap.get(this.drawMode)) throw new Error(`Draw Mode ${this.drawMode} must be defined if not initial pass`);
            // sends over the light info for each light
            const {locations} = this.drawMap.get(this.drawMode).shaderProperties
            this.lights.forEach((light: Light, i: number) => {
                const ambientLocation: string = "light[" + i + "].ambient";
                const diffuseLocation: string = "light[" + i + "].diffuse";
                const specularLocation: string = "light[" + i + "].specular";
                const lightPosLocation: string = "light[" + i + "].position";
                const lightDirLocation: string = "light[" + i + "].direction";
                const lightCutoffLocation: string = "light[" + i + "].cutoff";

                this.gl.uniform3fv(locations.getUniformLocation(ambientLocation), light.ambient);
                this.gl.uniform3fv(locations.getUniformLocation(diffuseLocation), light.diffuse);
                this.gl.uniform3fv(locations.getUniformLocation(specularLocation), light.specular);
                this.gl.uniform4fv(locations.getUniformLocation(lightPosLocation), light.position);
                this.gl.uniform4fv(locations.getUniformLocation(lightDirLocation), vec4.subtract(vec4.create(), light.spotTarget, light.position));
                this.gl.uniform1f(locations.getUniformLocation(lightCutoffLocation), light.spotCutoff);
            });
        }
        return this.lights.length;
    }
}
